// src/components/ClientList.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useDebounce from '../hooks/useDebounce';
import ClientCreateModal from './ClientCreateModal';
import { useAuthContext } from '../context/AuthContext';
import { IoAddCircle, IoPerson, IoMail, IoChevronForward, IoCheckmarkCircle, IoCloseCircle, IoTimeOutline, IoSearch } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import Spinner from './Spinner';
import { toast } from 'react-hot-toast';

const CLIENTS_PER_PAGE = 20;

const ClientList = () => {
  const { user } = useAuthContext();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // --- INICIO DE LA CORRECCIÓN ---

  // Este useEffect se dispara SOLO cuando el término de búsqueda cambia (después del debounce).
  // Su única responsabilidad es resetear la paginación a la primera página.
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);


  // Este useEffect se dispara cuando la página actual cambia O cuando el término de búsqueda cambia.
  // Es el encargado de hacer todas las peticiones a la API.
  useEffect(() => {
    const fetchClients = async () => {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');

      if (!token) {
        setError('No estás autenticado.');
        setLoading(false);
        return;
      }

      try {
        const listParams = {
          search: debouncedSearchTerm,
          page: currentPage,
          limit: CLIENTS_PER_PAGE,
        };
        const countParams = { search: debouncedSearchTerm };

        const [clientsResponse, countResponse] = await Promise.all([
          axios.get('http://127.0.0.1:8000/admin/clients', { headers: { Authorization: `Bearer ${token}` }, params: listParams }),
          axios.get('http://127.0.0.1:8000/admin/clients/count', { headers: { Authorization: `Bearer ${token}` }, params: countParams })
        ]);
        
        setClients(clientsResponse.data);
        setTotalPages(Math.ceil(countResponse.data.total / CLIENTS_PER_PAGE));
      } catch (err) {
        setError(err.response?.data?.detail || 'Error al cargar los clientes.');
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [currentPage, debouncedSearchTerm]); // Esta es la dependencia clave.

 const handleClientCreated = (newClient) => {
  // Para una UX instantánea, añadimos el cliente a la lista actual.
  // Luego podríamos recargar para asegurar la paginación, pero esto es más rápido.
  setClients(prev => [newClient, ...prev]);
};
const handleChangeStatus = async (clientId, newStatus) => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.put(
      `http://127.0.0.1:8000/admin/clients/${clientId}/status`,
      { status: newStatus },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    // Actualiza el cliente específico en la lista para reflejar el cambio al instante
    setClients(prevClients => 
      prevClients.map(c => c.id === clientId ? response.data : c)
    );
  } catch (err) {
    setError('Error al cambiar el estado del cliente.');
    console.error(err);
  }
};

const handleSelfAssign = async (clientId) => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.put(
      `http://127.0.0.1:8000/coach/clients/${clientId}/self-assign`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setClients((prevClients) =>
      prevClients.map((c) => (c.id === clientId ? response.data : c))
    );
    toast.success('Cliente autoasignado al coach.');
  } catch (err) {
    toast.error(err.response?.data?.detail || 'No se pudo autoasignar el cliente.');
  }
};

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

const statusConfig = {
    active:   { label: 'Activo',   classes: 'bg-green-500/20 text-green-400 border border-green-500/40',  icon: <IoCheckmarkCircle size={14} /> },
    inactive: { label: 'Inactivo', classes: 'bg-red-500/20 text-red-400 border border-red-500/40',        icon: <IoCloseCircle size={14} /> },
    pending:  { label: 'Pendiente',classes: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40', icon: <IoTimeOutline size={14} /> },
  };

  const getInitials = (name = '') =>
    name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

  if (loading) return <Spinner />;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div className="w-full min-h-full container mx-auto p-4 md:p-8">
      {isModalOpen && (
        <ClientCreateModal
          onClose={() => setIsModalOpen(false)}
          onClientCreated={handleClientCreated}
        />
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6 gap-3">
        <h1 className="text-2xl md:text-4xl font-bold text-white">
          Clientes
        </h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center shrink-0 bg-marizan-red hover:bg-red-700 text-white font-bold py-2 px-4 rounded-xl transition-colors"
        >
          <IoAddCircle size={20} className="mr-2" />
          Nuevo Cliente
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <IoSearch size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Buscar por nombre o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-gray-800 text-white rounded-xl border border-gray-700 focus:outline-none focus:border-marizan-red"
        />
      </div>

      {/* Cards grid */}
      {clients.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map(client => {
            const st = statusConfig[client.status] || statusConfig.inactive;
            return (
              <div
                key={client.id}
                className="bg-gray-800/60 border border-gray-700 rounded-2xl p-4 flex flex-col gap-3 hover:border-gray-500 transition-colors"
              >
                {/* Top row: avatar + name + status */}
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-marizan-red flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {getInitials(client.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">{client.name}</p>
                    <p className="text-gray-400 text-xs truncate flex items-center gap-1">
                      <IoMail size={11} />{client.email}
                    </p>
                  </div>
                  <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold shrink-0 ${st.classes}`}>
                    {st.icon}{st.label}
                  </span>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 pt-1 border-t border-gray-700">
                  <Link
                    to={`/clients/${client.id}`}
                    className="flex-1 flex items-center justify-center gap-1 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium py-2 rounded-lg transition-colors"
                  >
                    Ver / Editar <IoChevronForward size={14} />
                  </Link>

                  {user?.role === 'coach' && client.assigned_coach_id !== user?.id && (
                    <button
                      onClick={() => handleSelfAssign(client.id)}
                      className="flex-1 text-sm font-medium py-2 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 transition-colors"
                    >
                      Autoasignarme
                    </button>
                  )}

                  {client.status !== 'active' && (
                    <button
                      onClick={() => handleChangeStatus(client.id, 'active')}
                      className="flex-1 text-sm font-medium py-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors"
                    >
                      Activar
                    </button>
                  )}
                  {client.status !== 'inactive' && (
                    <button
                      onClick={() => handleChangeStatus(client.id, 'inactive')}
                      className="flex-1 text-sm font-medium py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
                    >
                      Desactivar
                    </button>
                  )}
                  {client.status !== 'pending' && (
                    <button
                      onClick={() => handleChangeStatus(client.id, 'pending')}
                      className="flex-1 text-sm font-medium py-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 transition-colors"
                    >
                      Pendiente
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-gray-500">
          <IoPerson size={48} className="mb-3 opacity-30" />
          <p className="text-lg">No se encontraron clientes</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center mt-8 text-white">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="bg-gray-700 hover:bg-gray-600 font-bold py-2 px-5 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Anterior
          </button>
          <span className="text-gray-400 text-sm">Página {currentPage} de {totalPages}</span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="bg-gray-700 hover:bg-gray-600 font-bold py-2 px-5 rounded-xl disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default ClientList;